import { v4 as uuidv4 } from 'uuid';
import type {
  DriveLabel,
  LabelCopyMode,
  LabelDraftModel,
  LabelFieldType,
} from '../../../../api/drive-labels-api';
import {
  COPY_MODES,
  CreateLabelForm,
  FIELD_TYPE_CONFIG,
  LabelFieldForm,
  LabelOptionForm,
} from './label-schema';

/** Converts form values to the payload the server expects (drops client-only keys). */
export function toDraftModel(values: CreateLabelForm): LabelDraftModel {
  return {
    title: values.title.trim(),
    description: values.description.trim(),
    learnMoreUri: values.learnMoreUri.trim(),
    copyMode: values.copyMode,
    driveEnabled: values.driveEnabled,
    gmailEnabled: values.gmailEnabled,
    fields: values.fields.map((field) => ({
      id: field.id,
      type: field.type,
      name: field.name.trim(),
      required: field.required,
      dateFormat: field.dateFormat,
      multipleSelections: field.multipleSelections,
      // Only disabled items carry the flag; Google's state (enabled) is the default otherwise.
      ...(field.disabled ? { disabled: true } : {}),
      options: FIELD_TYPE_CONFIG[field.type].hasOptions
        ? field.options.map((option) => ({
            id: option.id,
            name: option.name.trim(),
            description: option.description.trim(),
            badgeColor: field.type === 'BADGE_LIST' ? option.badgeColor : '',
            ...(option.disabled
              ? { disabled: true, showInApply: option.showInApply !== false }
              : {}),
          }))
        : [],
    })),
  };
}

/**
 * The default name for a new field: the type's name, then "<name> 2", "<name> 3", ... for the next
 * fields of that type, skipping names already in use.
 */
export function nextFieldName(
  type: LabelFieldType,
  fields: Pick<LabelFieldForm, 'name'>[]
) {
  const base = FIELD_TYPE_CONFIG[type].label;
  const used = new Set(fields.map((f) => f.name.trim()));
  if (!used.has(base)) {
    return base;
  }
  let n = 2;
  while (used.has(`${base} ${n}`)) {
    n++;
  }
  return `${base} ${n}`;
}

/** The default name for a new option: "Option n", skipping names already in use. */
/** Next unused default name, e.g. "Option 3", or "Badge option 3" for badge lists. */
export function nextOptionName(
  options: Pick<LabelOptionForm, 'name'>[],
  base = 'Option'
) {
  const used = new Set(options.map((o) => o.name.trim()));
  let n = options.length + 1;
  while (used.has(`${base} ${n}`)) {
    n++;
  }
  return `${base} ${n}`;
}

export function newOption(name: string): LabelOptionForm {
  return { key: uuidv4(), id: '', name, description: '', badgeColor: '' };
}

export function newField(
  type: LabelFieldType,
  fields: Pick<LabelFieldForm, 'name'>[]
): LabelFieldForm {
  return {
    key: uuidv4(),
    id: '',
    type,
    name: nextFieldName(type, fields),
    required: false,
    dateFormat: 'LONG_DATE',
    multipleSelections: false,
    options: FIELD_TYPE_CONFIG[type].hasOptions
      ? [newOption(type === 'BADGE_LIST' ? 'Badge option 1' : 'Option 1')]
      : [],
  };
}

/**
 * Returns the pasted option names that still fit in a field, given how many options it already
 * has and the per-field limit.
 */
export function optionsThatFit(
  names: string[],
  existingCount: number,
  maxOptions: number
): string[] {
  return names.slice(0, Math.max(0, maxOptions - existingCount));
}

/** Splits pasted content (e.g. a spreadsheet column or row) into option names. */
export function parsePastedOptions(content: string): string[] {
  return content
    .split(/\r?\n|\t/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function colorToHex(
  color?: {
    red?: number | null;
    green?: number | null;
    blue?: number | null;
  } | null
) {
  if (!color) {
    return '';
  }
  const channel = (value?: number | null) =>
    Math.round((value ?? 0) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(color.red)}${channel(color.green)}${channel(color.blue)}`;
}

/**
 * Works out a Google label field's Create Label type: multi or single select (badge list when its
 * options carry a badge config), number, date, person or text. Also used by the Label Manager drawer.
 */
export function detectFieldType(
  field: NonNullable<DriveLabel['fields']>[number]
): LabelFieldType {
  if (field.selectionOptions) {
    if (field.selectionOptions.listOptions) {
      return 'MULTI_SELECT';
    }
    // A badge config makes the field badged even without a color ("No fill").
    const badged = (field.selectionOptions.choices ?? []).some(
      (choice) => choice.properties?.badgeConfig
    );
    return badged ? 'BADGE_LIST' : 'SINGLE_SELECT';
  }
  if (field.integerOptions) return 'NUMBER';
  if (field.dateOptions) return 'DATE';
  if (field.userOptions) return 'PERSON';
  return 'TEXT';
}

/**
 * Form values for cloning a label: everything the label has, named "<name>_Clone" like other gPanel
 * clones. Field and option ids are cleared so Google creates new ones instead of pointing at the
 * source label's, and nothing is published yet, so every field and option stays editable.
 */
export function getCloneLabelDefaultValues(label: DriveLabel): CreateLabelForm {
  const values = fromGoogleLabel(label);
  return {
    ...values,
    title: `${values.title}_Clone`,
    // The clone starts fresh: nothing published or disabled, as before Edit Label tracked those.
    fields: values.fields.map(
      ({ disabled: _disabled, savedDisabled: _savedDisabled, ...field }) => ({
        ...field,
        id: '',
        published: false,
        options: field.options.map(
          ({
            disabled: _optionDisabled,
            savedDisabled: _optionSavedDisabled,
            showInApply: _showInApply,
            ...option
          }) => ({
            ...option,
            id: '',
            published: false,
          })
        ),
      })
    ),
  };
}

/**
 * Whether a label, field or option is live in Google. Anything not marked as an unpublished draft
 * counts as published, so a missing state is treated as the stricter case, like the server.
 */
export function isPublishedLifecycle(
  lifecycle: { state?: string | null } | undefined | null
): boolean {
  return lifecycle?.state !== 'UNPUBLISHED_DRAFT';
}

/**
 * Builds form values from a label returned by the server. After a save, Google returns fields and
 * options in the same order as the form, so the previous values (when given) are matched by
 * position to keep each item's client key and field type. A badge list with no colors looks like
 * a single select in Google's data, so the previous type wins.
 */
/** Whether Google has a field or option disabled, so users can't apply or select it. */
export function isDisabledLifecycle(
  lifecycle: { state?: string | null } | undefined | null
): boolean {
  return lifecycle?.state === 'DISABLED';
}

export function fromGoogleLabel(
  label: DriveLabel,
  previous?: CreateLabelForm
): CreateLabelForm {
  const enabledApps = new Set(
    (label.enabledAppSettings?.enabledApps ?? []).map((app) => app.app)
  );
  const copyMode = label.appliedLabelPolicy?.copyMode as LabelCopyMode;
  // Nothing on a label that was never published is live yet, whatever its items report. Only a
  // label Google reports as published or disabled locks anything; the server checks again.
  const labelState = label.lifecycle?.state;
  const labelPublished =
    labelState === 'PUBLISHED' || labelState === 'DISABLED';

  return {
    title: label.properties?.title ?? '',
    description: label.properties?.description ?? '',
    learnMoreUri: label.learnMoreUri ?? '',
    driveEnabled: enabledApps.has('DRIVE'),
    gmailEnabled: enabledApps.has('GMAIL'),
    copyMode: COPY_MODES.includes(copyMode)
      ? copyMode
      : previous?.copyMode ?? 'COPY_APPLIABLE',
    fields: (label.fields ?? []).map((field, fieldIndex) => {
      const previousField = previous?.fields[fieldIndex];
      const type = previousField?.type ?? detectFieldType(field);
      return {
        key: previousField?.key ?? uuidv4(),
        id: field.id ?? '',
        type,
        name: field.properties?.displayName ?? '',
        required: !!field.properties?.required,
        dateFormat:
          field.dateOptions?.dateFormatType === 'SHORT_DATE'
            ? 'SHORT_DATE'
            : 'LONG_DATE',
        // List options are what make a Person field multi-select in Google.
        multipleSelections: !!field.userOptions?.listOptions,
        published: labelPublished && isPublishedLifecycle(field.lifecycle),
        savedName: field.properties?.displayName ?? '',
        ...(isDisabledLifecycle(field.lifecycle)
          ? { disabled: true, savedDisabled: true }
          : {}),
        options: (field.selectionOptions?.choices ?? []).map(
          (choice, optionIndex) => ({
            key: previousField?.options[optionIndex]?.key ?? uuidv4(),
            id: choice.id ?? '',
            name: choice.properties?.displayName ?? '',
            description: choice.properties?.description ?? '',
            badgeColor: colorToHex(choice.properties?.badgeConfig?.color),
            published: labelPublished && isPublishedLifecycle(choice.lifecycle),
            // Google leaves false out, so a disabled option without showInApply is hidden.
            ...(isDisabledLifecycle(choice.lifecycle)
              ? {
                  disabled: true,
                  savedDisabled: true,
                  showInApply: !!choice.lifecycle?.disabledPolicy?.showInApply,
                }
              : {}),
          })
        ),
      };
    }),
  };
}
